<script setup lang="ts">
import { storeToRefs } from "pinia";
import Button from "~/components/ui/Button.vue";
import Select from "~/components/ui/Form/Select.vue";
import Icon from "~/components/ui/Icon.vue";
import { useBetslip } from "~/store/betslip";
import { useSettings } from "~/store/settings";
import { ISport } from "~/types/sports/sport";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import t from "~/utils/translate";
import { mdiFilterOutline } from "@mdi/js";
import { useBetbook } from "~/store/betbook";
import Dropdown from "~/components/ui/Form/Dropdown.vue";
import NavIcon from "../Nav/NavIcon.vue";
import LiveIndicator from "./LiveIndicator.vue";

interface Props {
	sports: ISport[] | undefined;
	liveOnly?: boolean;
	isEsports: boolean;
	isLoading?: boolean;
}

const store = useBetbook();
const props = defineProps<Props>();
const route = useRoute();
const settings = useSettings();
const betslip = useBetslip();
const { personalizedMenu } = storeToRefs(betslip);

const sportId = computed(() => {
	if (route.params.sportId) {
		return route.params.sportId;
	}
});

const sport = computed(() => {
	const sports = props.sports?.filter((x) => x.sportType === "Sport");

	if (sports && sports[0]) {
		let val;
		if (sportId.value) {
			val = sports.find((x) => x.sportId === sportId.value);
		} else {
			val = sports[0];
		}
		return val;
	}
});

const filteredSports = computed(() => {
	const defaultSports = props.sports && props.sports.filter((sport) => sport.sportType === "Sport" && sport.shouldDisplayCarousel);

	if (personalizedMenu.value.length > 0) {
		personalizedMenu.value.forEach((sportId) => {
			const sport = props.sports?.find((sport) => sport.sportId === sportId);
			if (sport && defaultSports) {
				defaultSports.push(sport);
			}
		});
	}
	return defaultSports;
});

const handleSportClick = (item: string) => {
	const sport = filteredSports.value?.find((x) => x.sportId === item);
	if (sport) {
		if (settings.isNativeApp && settings.hasNativeLobby && sport.redirectURL?.includes("lobby"))
			return useNuxtApp().$messageHandler({
				message: "navigateTo",
				data: {
					path: sport.redirectURL,
					vertical: sport.name,
				},
			});

		const router = useRouter();

		if (sport.redirectURL) return router.push(sport.redirectURL);
		router.push(
			sportUrlBuilder({
				sport: sport.sportId,
				liveOnly: props.liveOnly,
				fixtureType: sport.hasOutrights && !sport.hasHighlights ? "outrights" : "highlights",
			})
		);
	}
};
const { fixtureType } = route.params;

const filterApplied = computed(
	() =>
		(!fixtureType || fixtureType === "highlights") &&
		(store.filters.sortOrder !== "League" ||
			!!store.filters.regionAndLeagueIds ||
			!!store.filters.fromStartEpoch ||
			!!store.filters.toStartEpoch ||
			!!store.filters.odds)
);

const dropdownOptions = computed(() => {
	if (filteredSports.value) {
		return filteredSports.value.map((s) => {
			return {
				name: t(s.sportId, { identifier: "sports." }).replace("|", " / "),
				value: s.sportId,
				image: `https://cms1.betwayafrica.com/medialibraries/content.gmgamingsystems.com/Synapse/icons/${s.sportId
					.replace("esports-", "")
					.replace("---", "-")}.png`,
			};
		});
	} else return [];
});
</script>

<template>
	<div :class="['w-full grid grid-cols-2 gap-2 mb-2', isLoading && 'opacity-50 pointer-events-none']">
		<Dropdown :model-value="sport?.sportId" @update:model-value="(val) => handleSportClick(val)" label="" noFloatingLabel :options="dropdownOptions">
			<div v-if="sport" class="flex gap-2">
				<NavIcon :width="24" :height="24" :src="`/icons/${sport.sportId.replace('esports-', '').replace('---', '-')}.png`" :alt="sport.name" />
				<div class="flex flex-col">
					<strong :class="sport.liveInPlayCount > 0 ? 'text-xs leading-[13px]' : 'text-sm'">
						{{ t(sport?.sportId ?? "", { identifier: "sports." }).replace("|", " / ") }}
					</strong>
					<div class="flex flex-grow-0">
						<LiveIndicator v-if="sport.liveInPlayCount > 0" :liveCount="sport.liveInPlayCount" />
					</div>
				</div>
			</div>
		</Dropdown>
		<div
			@click="store.filterModal = 'leagues'"
			:class="[
				'relative w-full  rounded flex justify-between px-4 border items-center  cursor-pointer font-bold',
				filterApplied
					? 'bg-dark-600 gap-1 font-bold text-light-50 hover:bg-dark-600 border-dark-600'
					: 'bg-light-50 dark:bg-dark-800 border-light-500 dark:border-dark-600',
			]"
		>
			{{ t("all-leagues") }}
			<Icon :class="['w-6 h-6', filterApplied ? 'fill-identity' : 'fill-dark-800 dark:fill-light-50']" :path="mdiFilterOutline" />
		</div>
	</div>
</template>
